import React, { useEffect, useState } from 'react';
import { history, useIntl } from 'umi';
import { Button, notification } from 'tea-component';
import { getBasePath } from '@/utils/utils';
import { isAllowDomain } from '../login/_config/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import Loading from '@home/components/Loading';
import { activateAccessCode } from '@home/services/login';
import './index.scss';

/**
 * 访问码登录查询参数接口
 */
interface IAccessCodeLoginQuery {
  loginCode?: string;
  redirect?: string;
  error?: string;
}

/**
 * 访问码登录页面
 * 通过 URL 参数中的 loginCode 自动登录
 * 显示加载状态，成功后跳转，失败后重定向到登录页
 */
const AccessCodeLogin: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const isEn = locale === LANGUAGE_EN;

  useEffect(() => {
    handleAccessCodeLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 处理访问码登录
   */
  const handleAccessCodeLogin = async (): Promise<void> => {
    const query = (history.location.query || {}) as IAccessCodeLoginQuery;
    const { loginCode, redirect } = query;

    // 验证访问码是否存在
    if (!loginCode || typeof loginCode !== 'string' || !loginCode.trim()) {
      const errorMsg = getName(isEn, 'Invalid Access Code', '登入码无效');
      setError(errorMsg);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await activateAccessCode(loginCode.trim());

      // 手动设置 token（因为后端返回 token 在响应体而非响应头）
      if (response?.data?.token) {
        try {
          const { setLogin } = await import('@/utils/utils');
          setLogin(response.data.token);
        } catch (importError) {
          console.error('Failed to load utils module:', importError);
          const systemError = getName(isEn, 'System error, please try again', '系统错误，请重试');
          notification.error({
            description: systemError,
            duration: 3000,
          });
          setError(getName(isEn, 'System error', '系统错误'));
          setLoading(false);
          return;
        }
      }

      // 登录成功，安全的重定向处理
      const safeRedirectUrl =
        redirect && typeof redirect === 'string' && isAllowDomain(redirect)
          ? redirect
          : `${getBasePath()}/home`;

      window.location.replace(safeRedirectUrl);
    } catch (err: unknown) {
      const errorMessage = getName(isEn, 'Invalid Access Code', '登入码无效');

      // 安全的错误处理
      if (err && typeof err === 'object' && 'req' in err) {
        const error = err as { req?: string; code?: number; };
        notification.error({
          description: error.req || errorMessage,
          duration: 3000,
        });
      } else {
        notification.error({
          description: errorMessage,
          duration: 3000,
        });
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  /**
   * 返回登入页
   * @param errorKey - 错误键名（可选）
   */
  const backToLoginPage = (errorKey?: string): void => {
    const query = (history.location.query || {}) as IAccessCodeLoginQuery;
    const { redirect } = query;
    const newQuery: Record<string, string> = {};

    if (redirect && typeof redirect === 'string') {
      newQuery.redirect = redirect;
    }

    if (errorKey) {
      newQuery.error = errorKey;
    }

    history.replace({
      pathname: '/login',
      query: newQuery,
    });
  };

  return (
    <div className="dbh-access-login">
      {loading ? (
        <div className="dbh-access-login__loading">
          <Loading text={getName(isEn, 'Logging in...', '登入中...')} />
        </div>
      ) : (
        <div className="dbh-access-login__error">
          <span className="dbh-access-login__error-message">{error}</span>
          <Button type="link" onClick={() => backToLoginPage()}>
            {formatMessage({ id: 'dbh_login_goback_tologin' })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AccessCodeLogin;
